import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Users, CheckCircle, FileCheck, HelpCircle, FileText, AlertCircle, XCircle } from 'lucide-react';
import { candidatesApi } from '../../api/candidates';
import { jobsApi } from '../../api/jobs';
import { StatusIndicator } from '../candidates/StatusBadge';
import { Job } from './JobCard';
import CompleteJobButton from './CompleteJobButton';
import FinalReportModal from './CompletedJobs/FinalReportModal';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useDataRefresh } from '../../contexts/DataRefreshContext';

// Custom tooltip for consistent usage
const Tooltip = ({ children, content, className = "" }) => {
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const containerRef = React.useRef(null);
  
  const updateTooltipPosition = (e) => {
    setPosition({
      x: e.clientX,
      y: e.clientY
    });
  };
  
  return (
    <div 
      className={`relative group inline-block ${className}`} 
      ref={containerRef}
      onMouseMove={updateTooltipPosition}
    >
      {children}
      <div 
        className="fixed hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none shadow-lg" 
        style={{
          zIndex: 9999,
          top: position.y - 25,
          left: position.x,
          transform: 'translateX(-50%)'
        }}
      >
        {content}
      </div>
    </div>
  );
};

interface JobDetailsModalProps {
  job: Job;
  onClose: () => void;
  onJobCompleted?: (job: Job) => void;
}

const JobDetailsModal = ({ job, onClose, onJobCompleted }: JobDetailsModalProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { refreshJobs } = useDataRefresh();
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalReferences, setTotalReferences] = useState(0);
  const [showFinalReport, setShowFinalReport] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  
  // Close modal when Escape key is pressed
  useEscapeKey(onClose);
  const referenceProgress = totalReferences > 0 
    ? Math.round((job.referencesCompleted / totalReferences) * 100) 
    : 0;
  const daysUntilDue = Math.ceil((new Date(job.dueDate).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
  const isOverdue = daysUntilDue < 0;

  React.useEffect(() => {
    const loadCandidates = async () => {
      try {
        console.log('Loading candidates for job:', job);
        const data = await candidatesApi.getAll();
        const jobCandidates = data.filter(c => c.jobId === job.id);
        
        // Log detailed information to debug the issue
        console.log('Candidates found:', jobCandidates);
        console.log('Candidates with form filled:', jobCandidates.filter(c => c.form_filled_at));
        
        // Log reference completion details for each candidate
        jobCandidates.forEach(candidate => {
          const completedRefs = candidate.references ? 
            candidate.references.filter(ref => ref.filled_form_at).length : 0;
          const totalRefs = candidate.references ? candidate.references.length : 0;
          console.log(`Candidate ${candidate.full_name}: ${completedRefs}/${totalRefs} references completed`);
          
          // Log each reference status
          if (candidate.references) {
            candidate.references.forEach((ref, index) => {
              console.log(`Reference ${index + 1}: ${ref.full_name}, filled: ${!!ref.filled_form_at}`);
            });
          }
        });
        
        setCandidates(jobCandidates);
        
        // Calculate total references
        const totalRefs = jobCandidates.reduce((total, candidate) => {
          return total + (candidate.references ? candidate.references.length : 0);
        }, 0);
        setTotalReferences(totalRefs);
      } catch (error) {
        console.error('Failed to load candidates:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCandidates();
  }, [job.id]);

  const handleViewDetails = () => {
    onClose();
    // Add status=COMPLETED to the URL for completed jobs
    if (job.status === 'COMPLETED' || job.status === 'completed') {
      navigate(`/candidates?jobId=${job.id}&status=COMPLETED`);
    } else {
      navigate(`/candidates?jobId=${job.id}`);
    }
  };
  
  const handleCancelJob = async () => {
    setCancelLoading(true);
    try {
      await jobsApi.cancel(job.id);
      refreshJobs();
      onClose();
    } catch (error) {
      console.error('Failed to cancel job:', error);
    } finally {
      setCancelLoading(false);
      setShowCancelConfirmation(false);
    }
  };
  
  const isCompleted = job.status === 'COMPLETED' || job.status === 'completed';
  const isActive = !isCompleted;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-semibold">{job.title}</h2>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Overdue Warning */}
            {isOverdue && (
              <div className="mb-4 flex items-center bg-red-100 text-red-800 p-4 rounded-lg border border-red-200">
                <AlertCircle className="w-5 h-5 mr-2" />
                <div>
                  <div className="font-medium">{t('dashboard.activeJobs.priority.overdue')}</div>
                  <div className="text-sm">{t('dashboard.activeJobs.priority.overdueDescription')}</div>
                </div>
              </div>
            )}
            
            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg flex flex-col h-full">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <Users className="w-5 h-5" />
                  <span className="font-medium">{t('dashboard.jobDetailsModal.stats.candidates')}</span>
                </div>
                <div className="flex items-center justify-center flex-1">
                  <div className="text-2xl font-semibold">{job.candidates}</div>
                </div>
              </div>

              <div className="bg-indigo-50 p-4 rounded-lg flex flex-col h-full">
                <div className="flex items-center gap-2 text-indigo-600 mb-2">
                  <FileCheck className="w-5 h-5" />
                  <span className="font-medium">{t('dashboard.jobDetailsModal.stats.candidateForms')}</span>
                </div>
                <div className="flex flex-col items-center justify-center flex-1">
                  <div className="text-2xl font-semibold">
                    {candidates.length > 0 
                      ? Math.round((candidates.filter(c => c && 'form_filled_at' in c && c.form_filled_at).length / candidates.length) * 100)
                      : 0}%
                  </div>
                  <div className="text-sm text-indigo-600 mt-1">
                    {candidates.filter(c => c && 'form_filled_at' in c && c.form_filled_at).length}/{candidates.length} candidates
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg flex flex-col h-full">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{t('dashboard.jobDetailsModal.stats.referenceForms')}</span>
                </div>
                <div className="flex flex-col items-center justify-center flex-1">
                  <div className="text-2xl font-semibold">{referenceProgress}%</div>
                  <div className="flex items-center text-sm text-green-600 mt-1">
                    <span>{job.referencesCompleted}/{totalReferences}</span>
                    <Tooltip 
                      className="ml-1 cursor-help" 
                      content={t('dashboard.jobDetailsModal.stats.tooltipReferences')}
                    >
                      <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Section */}
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">{t('dashboard.jobDetailsModal.progress.title')}</span>
                <div className="flex items-center text-sm text-gray-500">
                  <span>{job.referencesCompleted}/{totalReferences} {t('dashboard.jobDetailsModal.progress.completed')}</span>
                  <Tooltip 
                    className="ml-1 cursor-help" 
                    content={t('dashboard.jobDetailsModal.stats.tooltipReferences')}
                  >
                    <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                  </Tooltip>
                </div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${referenceProgress}%` }}
                />
              </div>
            </div>

            {/* Candidates List */}
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 border-b px-4 py-3">
                <h3 className="text-lg font-medium">{t('dashboard.jobDetailsModal.candidates.title')}</h3>
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-gray-500">{t('dashboard.jobDetailsModal.candidates.loading')}</p>
                </div>
              ) : (
                <div className="max-h-[300px] overflow-y-auto">
                  {candidates.length > 0 ? (
                    <div className="divide-y">
                      {candidates.map((candidate) => {
                        // Check if candidate has filled the form
                        const hasFilledForm = candidate && 'form_filled_at' in candidate && candidate.form_filled_at;
                        
                        // Count completed references - explicitly check for filled_form_at truthy value
                        const completedRefs = candidate.references ? 
                          candidate.references.filter(ref => ref.filled_form_at).length : 0;
                          
                        // Get total references
                        const totalRefs = candidate.references ? candidate.references.length : 0;
                        
                        // If all references are complete or there are no references required,
                        // don't show as having pending references
                        const hasPendingReferences = completedRefs < totalRefs && totalRefs > 0;
                        
                        // Candidate is complete if form is filled and all references are complete
                        const isComplete = hasFilledForm && !hasPendingReferences;
                        
                        return (
                          <div key={candidate.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-start gap-3">
                              <Tooltip
                                content={
                                  !hasFilledForm ? 
                                    <>{t('dashboard.jobDetailsModal.candidates.tooltip.pendingCandidate')}</> : 
                                  hasPendingReferences ? 
                                    <>{t('dashboard.jobDetailsModal.candidates.tooltip.pendingReferences', { date: new Date(candidate.form_filled_at).toLocaleDateString() })}</> : 
                                    <>{t('dashboard.jobDetailsModal.candidates.tooltip.done')}</>
                                }
                              >
                                <StatusIndicator 
                                  formFilled={hasFilledForm}
                                  referencesCompleted={completedRefs}
                                  referencesTotal={totalRefs}
                                />
                              </Tooltip>
                              <div>
                                <div className="font-medium text-gray-900">{candidate.full_name}</div>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className="text-sm text-gray-500">
                                    {t('dashboard.jobDetailsModal.candidates.references')}:
                                  </span>
                                  <div className="flex items-center">
                                    <span className="text-sm font-medium">{completedRefs}/{totalRefs}</span>
                                    {totalRefs > 0 && (
                                      <div className="ml-2 w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                          className="h-full bg-blue-500 rounded-full"
                                          style={{ width: `${(completedRefs / totalRefs) * 100}%` }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {!hasFilledForm ? (
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">{t('dashboard.jobDetailsModal.candidates.status.pendingCandidate')}</span>
                            ) : hasPendingReferences ? (
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">{t('dashboard.jobDetailsModal.candidates.status.pendingReferences')}</span>
                            ) : (
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{t('dashboard.jobDetailsModal.candidates.status.done')}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-gray-500">
                      {t('dashboard.jobDetailsModal.candidates.empty')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap justify-between gap-3 p-4 border-t bg-gray-50">
            {/* Left side buttons */}
            <div>
              {isActive && (
                <button
                  onClick={() => setShowCancelConfirmation(true)}
                  disabled={cancelLoading}
                  className="flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-red-300 rounded-lg transition-colors"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  {cancelLoading ? t('common.processing') : t('common.cancel')}
                </button>
              )}
            </div>
            
            {/* Right side buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.close')}
              </button>
              
              {isCompleted && (
                <button
                  onClick={() => setShowFinalReport(true)}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {t('dashboard.finalReport.seeFinalReport')}
                </button>
              )}
              
              <button
                onClick={handleViewDetails}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                {t('common.viewDetails')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showFinalReport && (
        <FinalReportModal
          jobId={job.id.toString()}
          jobTitle={job.title}
          onClose={() => setShowFinalReport(false)}
        />
      )}

      {showCancelConfirmation && (
        <ConfirmationDialog
          title={t('dashboard.jobs.cancel.title')}
          message={t('dashboard.jobs.cancel.message', { jobTitle: job.title })}
          onConfirm={handleCancelJob}
          onCancel={() => setShowCancelConfirmation(false)}
        />
      )}
    </>
  );
};

export default JobDetailsModal;